/**
 * Phase I-B.2A — BuilderProjectsTab (slim shell).
 *
 * Previously this file held the entire Project form inline (~900 LOC).
 * The form has been EXTRACTED into the shared `ProjectForm` component
 * (`/app/frontend/src/components/project/ProjectForm.jsx`) so Admin
 * and Builder share one ownership surface.
 *
 * This file now renders ONLY:
 *   • The "My Projects" list with Edit / Delete actions
 *   • An "Add Project" toggle that opens ProjectForm in `create` mode
 *   • Edit click → opens ProjectForm in `edit` mode for that row
 *
 * Permissions are enforced server-side (see /app/backend/server.py
 * /builder/projects routes + /api/projects/{id}/configurations).
 */
import React, { useEffect, useState } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useDataSync, emitDataChange } from "../lib/dataSync";
import { Button } from "../components/ui/button";
import { Plus, Pencil, Trash2, Clock, CheckCircle, XCircle, Building2 } from "lucide-react";
import { toast } from "sonner";
import ProjectForm from "../components/project/ProjectForm";
import { useProjectStatusLabels } from "../lib/projectSchemaApi";

const APPROVAL_VISUAL = {
  pending:   { icon: <Clock      size={11} />, cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
  approved:  { icon: <CheckCircle size={11} />, cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]" },
  rejected:  { icon: <XCircle    size={11} />, cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]" },
};

export default function BuilderProjectsTab({ readOnly = false, editProject = null, onSavedExternal = null }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [mode, setMode] = useState(editProject ? "edit" : null); // null | "create" | "edit"
  const [editingProject, setEditingProject] = useState(editProject || null);
  const { statusLabel } = useProjectStatusLabels();

  // Externally controlled edit (admin dialog re-entry).
  useEffect(() => {
    if (editProject) {
      setEditingProject(editProject);
      setMode("edit");
    }
  }, [editProject?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => api.get("/builder/my-projects").then((r) => setProjects(r.data || []));
  useEffect(() => { if (!editProject) load(); }, [editProject]);
  useDataSync(["projects"], load);

  const onFormSaved = (saved) => {
    setMode(null);
    setEditingProject(null);
    if (onSavedExternal) onSavedExternal(saved);
    else load();
    emitDataChange("projects", { action: "save", project_id: saved?.id });
  };
  const onFormCancel = () => {
    if (onSavedExternal) { onSavedExternal(null); return; }
    setMode(null);
    setEditingProject(null);
  };

  const removeProject = async (p) => {
    if (!confirm(`Delete project "${p.name}"?`)) return;
    try {
      await api.delete(`/builder/projects/${p.id}`);
      toast.success("Project deleted");
      emitDataChange("projects", { action: "delete", project_id: p.id });
      load();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Delete failed"));
    }
  };

  // ── Form mode ──
  if (mode) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-[12px]">
          <Building2 size={13} className="text-[#0044CC]" />
          <span className="font-semibold">{mode === "create" ? "Add Project" : `Edit Project — ${editingProject?.name}`}</span>
        </div>
        <ProjectForm
          mode={mode}
          project={editingProject}
          currentUser={user}
          onSaved={onFormSaved}
          onCancel={onFormCancel}
        />
      </div>
    );
  }

  // ── List + Add button ──
  return (
    <div className="space-y-4" data-testid="builder-projects-tab">
      {readOnly && (
        <div className="bg-[#FEF3C7] border border-[#FDE68A] text-[#92400E] text-[12px] p-3 rounded-sm">
          Read-only view. Only Builders/Admin can create/edit projects.
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-semibold tracking-tight">My Projects</h2>
          <p className="text-[12px] text-[#71717A]">{projects.length} project{projects.length === 1 ? "" : "s"}</p>
        </div>
        {!readOnly && (
          <Button onClick={() => { setEditingProject(null); setMode("create"); }} className="btn-brand rounded-sm" data-testid="builder-add-project">
            <Plus size={13} className="mr-1.5" /> Add Project
          </Button>
        )}
      </div>
      {projects.length === 0 ? (
        <div className="bg-white border border-dashed border-[#D4D4D8] rounded-sm p-8 text-center">
          <Building2 size={20} className="mx-auto text-[#71717A] mb-2" />
          <div className="text-[12px] text-[#71717A]">No projects yet. Click <span className="font-semibold">Add Project</span> to create one.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {projects.map((p) => (
            <div key={p.id} className="bg-white border border-[#E4E4E7] rounded-sm p-3 flex items-center justify-between" data-testid={`builder-project-${p.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {(p.images || []).slice(0, 1).map((img, i) => (
                  <img key={i} src={img} alt="" className="w-12 h-12 object-cover rounded-sm border border-[#E4E4E7]" />
                ))}
                <div className="min-w-0">
                  <div className="font-semibold text-[13px] truncate" data-testid={`builder-project-${p.id}-name`}>{p.name}</div>
                  <div className="text-[11px] text-[#71717A] flex items-center gap-2">
                    <span data-testid={`builder-project-${p.id}-status-label`}>{statusLabel(p.status)}</span>
                    {p.location && <><span>·</span><span className="truncate">{p.location}</span></>}
                    {p.approval_status && APPROVAL_VISUAL[p.approval_status] && (
                      <>
                        <span>·</span>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0 rounded-sm border text-[10px] font-semibold ${APPROVAL_VISUAL[p.approval_status].cls}`}>
                          {APPROVAL_VISUAL[p.approval_status].icon} {p.approval_status}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {!readOnly && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={() => { setEditingProject(p); setMode("edit"); }} data-testid={`my-project-edit-${p.id}`}>
                    <Pencil size={11} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-sm h-7 text-[11px] text-[#DC2626] hover:bg-[#FEE2E2]" onClick={() => removeProject(p)} data-testid={`my-project-delete-${p.id}`}>
                    <Trash2 size={11} className="mr-1" /> Delete
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
